import { Badge, Box, Divider, Flex, HStack, Link as ChakraLink, SimpleGrid, Text, Heading, useBreakpointValue } from '@chakra-ui/react';
import { CompactActionButton } from '@/components/ui/CompactActionButton';
import { motion, useReducedMotion, Variants } from 'framer-motion';
import { ImageCarousel, FriendAvatars } from '@/components/display';
import { TasteMatchPill } from '@/components/display/TasteMatchPill';
import { getRestaurantPillLabelProps } from '@/components/display/tasteMatchPillLabels';
import { CompatibilityModal } from '@/pages/restaurant/components/hero/CompatibilityModal';
import {
  BadgeCheck,
  BookmarkCheck,
  Share2,
  Smile,
  Sparkles,
  Utensils
} from 'lucide-react';
import React, { useState, useCallback, useRef } from 'react';
import { Link, type LinkProps } from '@tanstack/react-router';
import { Restaurant } from '@/types/restaurant';
import {
  getRestaurantGradient,
  getCompatibilityEmoji,
  getCompatibilityColor,
  hasValidAddressParts,
  getCompatibilityGlow,
  getCompatibilityLevel,
  getCompatibilityBorderColor,
  hasPersonalRating
} from '@/utils';
import { useOpenRestaurantProfile } from '@/hooks/useOpenRestaurantProfile';
import { useRestaurantReturnTarget } from '@/hooks/useRestaurantReturnTarget';
import { RestaurantRatingModal } from '@/modals/restaurant/RestaurantRatingModal';
import { AddToListModal } from '@/modals/lists';
import { getTasteMatchBand } from '@/utils/tasteMatchBand';
import { haptics, ImpactStyle } from '@/utils/haptics';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { FOCUS_VISIBLE_RING } from '@/pages/restaurant/components/actions/ActionButtons';
import { useRestaurantShare } from '@/hooks/useRestaurantShare';

// Motion components
const MotionBox = motion.create(Box);
const MotionBadge = motion.create(Badge);

// Animation variants
const cardVariants: Variants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: i * 0.06,
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
};

const pulseVariants: Variants = {
  animate: {
    scale: [1, 1.05, 1],
    boxShadow: [
      '0 0 0 0 var(--color-compat-high-glow-40)',
      '0 0 0 6px var(--color-compat-high-glow-0)',
      '0 0 0 0 var(--color-compat-high-glow-0)'
    ],
    transition: {
      repeat: Infinity,
      duration: 2,
      ease: 'easeInOut',
    },
  },
};

// Extended restaurant type with pre-calculated distance from Search.tsx.
// null means unmeasurable (unresolved origin or missing restaurant geometry) —
// distinct from undefined (distance not computed at all, e.g. non-search contexts).
interface RestaurantWithDistance extends Restaurant {
  _calculatedDistance?: number | null;
}

interface RestaurantCardProps {
  restaurant: RestaurantWithDistance;
  index?: number;
  /**
   * Optional `data-testid` for the card's root element. Lets a hosting page
   * (e.g. Search's Playwright hook) find each rendered card directly without
   * wrapping it in an extra Box — a wrapper breaks Grid `align-items: stretch`
   * row-height matching and the `.restaurant-card:nth-child(N)` staggered
   * entrance animation in `src/theme/variables.css` (D59 review finding).
   */
  testId?: string;
  /**
   * Whether the card plays its mount entrance (framer fade/rise plus the CSS
   * `cardEntrance`). Default true. Read once at first render, like the
   * return-morph flag. A host passes false for cards that were already on
   * screen when the list is restored (Search after back navigation), so the
   * list returns as the member left it instead of re-staggering in.
   */
  animateEntrance?: boolean;
}

// Discriminates which element within the card triggered navigation. Only
// 'card_body' remains (the name link and the card body share one path); the
// `trigger` property stays in the restaurant_card_clicked payload so the
// PostHog schema is stable.
type RestaurantCardClickTrigger = 'card_body';

function RestaurantCard({ restaurant, index = 0, testId, animateEntrance = true }: RestaurantCardProps) {
  // Cards only render on Search, which already warms the profile chunk once at
  // idle, so the hook's own mount warm-up is off here.
  const openRestaurantProfile = useOpenRestaurantProfile({ warm: false });
  const badgeSize = useBreakpointValue({ base: 14, lg: 18 }) ?? 14;
  const badgeTop = useBreakpointValue({ base: '-0.3em', lg: '-0.4em' }) ?? '-0.3em';
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [isAddToListModalOpen, setIsAddToListModalOpen] = useState(false);
  const [isCompatibilityModalOpen, setIsCompatibilityModalOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const photoRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  // Reverse shared-element morph (profile -> this card): the hook reads the
  // pending-target flag once at first render and claims the names in a layout
  // effect (src/utils/viewTransition.ts). The morph target skips its entrance
  // so the "new" snapshot sees the card at its final size/opacity.
  const isReturnTarget = useRestaurantReturnTarget(restaurant.id, photoRef, titleRef);
  // Frozen at first render: a card never re-enters because the prop flips later.
  const [skipEntrance] = useState(() => isReturnTarget || !animateEntrance);
  // Phase 1 spring/overshoot token (src/theme/variables.css --motion-spring-duration /
  // --motion-spring-easing), applied to the bookmark icon's fill/stroke transition —
  // this is a plain CSS `style` transition, so the CSS custom properties are used directly.
  const bookmarkIconTransition = prefersReducedMotion
    ? 'none'
    : 'fill var(--motion-spring-duration) var(--motion-spring-easing), stroke var(--motion-spring-duration) var(--motion-spring-easing)';

  // Get compatibility level for badge and glow effects
  const compatibilityLevel = getCompatibilityLevel(restaurant);
  const isHighestCompatibility = compatibilityLevel === 5;
  const glowShadow = getCompatibilityGlow(restaurant);
  const borderColor = getCompatibilityBorderColor(restaurant);

  // Haptic feedback handlers
  const handleRateClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    await haptics.impact(ImpactStyle.Light);
    setIsRatingModalOpen(true);
  }, []);

  // Share reuses the profile's share flow (src/hooks/useRestaurantShare.ts).
  // Haptic is fire-and-forget, NOT awaited: navigator.share needs transient
  // user activation and iOS Safari rejects it after an async gap.
  const shareRestaurant = useRestaurantShare(restaurant, 'search_results');
  const handleShareClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    void haptics.impact(ImpactStyle.Light);
    return shareRestaurant();
  }, [shareRestaurant]);

  const handleBookmarkClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Haptic fires here as tap-time feedback for opening the list picker; the
    // spring on the bookmark icon below fires later, decoupled in time, whenever
    // restaurant.isBookmarked actually flips (e.g. after the member picks a list
    // in AddToListModal). This gap is intentional, not a bug.
    await haptics.impact(ImpactStyle.Light);
    setIsAddToListModalOpen(true);
  }, []);

  // Format pre-calculated distance for display. null/undefined means the
  // distance is unmeasurable (unresolved origin or missing restaurant
  // geometry) — rendered as the "No distance" affordance, never a fallback
  // number (D41c Phase 4, ui-ux-designer consult).
  const getFormattedDistance = () => {
    const distance = restaurant._calculatedDistance;
    if (distance === undefined || distance === null) return null;

    // Format distance to avoid scientific notation
    if (distance >= 1000) {
      // For distances 1000+ miles, show in thousands (e.g., "2.8k")
      return (distance / 1000).toFixed(1) + 'k mi';
    } else if (distance >= 100) {
      // For distances 100-999 miles, show whole number (e.g., "156")
      return Math.round(distance).toString() + ' mi';
    } else if (distance >= 10) {
      // For distances 10-99 miles, show one decimal (e.g., "23.5")
      return distance.toFixed(1) + ' mi';
    } else {
      // For distances under 10 miles, show two decimals (e.g., "1.28")
      return distance.toFixed(2) + ' mi';
    }
  }
  const formattedDistance = getFormattedDistance();

  const navigateToRestaurantProfile = (trigger: RestaurantCardClickTrigger) => {
    // Track restaurant card click
    analytics.capture(AnalyticsEvents.RESTAURANT_CARD_CLICKED, {
      restaurant_id: restaurant.id,
      restaurant_name: restaurant.name,
      source: 'search_results',
      trigger,
      compatibility_level: compatibilityLevel,
    });
    // Seed + chunk/reduced-motion/cache gate + shared-element names + navigate
    // all live in the shared hook (D45c Phase 4 morph; see its doc comment).
    openRestaurantProfile(restaurant.id, { seedFrom: restaurant, photoRef, titleRef });
  }

  // The restaurant name is a real link so keyboard/screen-reader members have
  // a path to the profile (the card root is a click-only div). preventDefault
  // stops the anchor's own router navigation; routing through
  // navigateToRestaurantProfile keeps analytics + the view transition on one
  // path. stopPropagation prevents the card root's onClick from firing a
  // second capture/navigate.
  // Typed separately: Chakra's `as={Link}` polymorphism erases TanStack's
  // route-param generics, so the typed props are spread in.
  const nameLinkProps: LinkProps = {
    to: '/restaurants/$restaurantId',
    params: { restaurantId: restaurant.id },
  };

  const handleNameLinkClick = (e: React.MouseEvent) => {
    // Modified clicks (new tab/window/download) and non-primary buttons are
    // left to the browser: only keep the card root from also navigating.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
      e.stopPropagation();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    navigateToRestaurantProfile('card_body');
  };

  return (
    <MotionBox
      custom={index}
      variants={cardVariants}
      initial={skipEntrance ? false : 'hidden'}
      animate="visible"
      whileHover={{ y: -4, boxShadow: `${glowShadow}, 0 10px 30px var(--color-overlay-15)` }}
      whileTap={{ scale: 0.98 }}
      borderWidth="2px"
      borderColor={borderColor}
      overflow="hidden"
      cursor="pointer"
      mb={{ base: 3, lg: 6 }}
      color="var(--card-text)"
      display="flex"
      flexDirection="column"
      className="restaurant-card"
      // Marks the reverse-morph target so variables.css skips the CSS
      // cardEntrance animation (framer's `initial={false}` doesn't cover it).
      data-vt-return={isReturnTarget ? '' : undefined}
      // Same for restored cards (animateEntrance=false): framer's
      // `initial={false}` doesn't cover the CSS entrance either.
      data-entrance-skipped={skipEntrance ? '' : undefined}
      data-restaurant-id={restaurant.id}
      data-testid={testId}
      borderRadius={'var(--r-card)'}
      boxShadow={glowShadow}
      bg="var(--card-bg)"
      style={{ transition: 'border-color 0.3s ease' }}
      onClick={() => navigateToRestaurantProfile('card_body')}
    >
      <Box ref={photoRef} position="relative" flexShrink={0} className="restaurant-image" h="200px">
        <ImageCarousel
          images={restaurant.images || []}
          alt={restaurant.name}
          height="200px"
          currentIndex={currentImageIndex}
          onIndexChange={setCurrentImageIndex}
          fallbackContent={
            <Box h="200px" w="100%" bg={getRestaurantGradient(restaurant.id)} display="flex" alignItems="center" justifyContent="center" className="restaurant-image">
              <Utensils size={48} color="white" opacity={0.6} />
            </Box>
          }
        />
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          // D45c Phase 3 color-echo: tints the scrim with the restaurant's
          // own cover-photo color (Phase 6, paire-api) when available,
          // blended into the original contrast overlay rather than
          // replacing it. Falls back to the neutral placeholder token when
          // coverPhotoColor is null/undefined — same value the loading
          // placeholder uses, so this picks up real color automatically
          // once Phase 6 ships, no code change needed.
          background={`linear-gradient(color-mix(in srgb, ${restaurant.coverPhotoColor ?? 'var(--card-surface-2)'} 45%, var(--color-overlay-30)), transparent)`}
          pointerEvents="none"
          zIndex={1}
        />
        {/* Always rendered (CD, 2026-10-04): a restaurant with no personal
            TasteMatch shows the grey "Unsure TasteMatch" badge, with or without
            a public/Google rating, matching RestaurantHero. */}
        <TasteMatchPill
          emojiSrc={getCompatibilityEmoji(restaurant).icon}
          emojiAlt={getCompatibilityEmoji(restaurant).label}
          tierColor={getCompatibilityColor(restaurant)}
          {...getRestaurantPillLabelProps(
            restaurant,
            hasPersonalRating(restaurant)
              ? getTasteMatchBand(restaurant.rating, 'taste_match').tierWord
              : undefined,
          )}
          onClick={() => setIsCompatibilityModalOpen(true)}
        />
        {/* Best Match badge for highest compatibility */}
        {isHighestCompatibility && (
          <MotionBadge
            variants={pulseVariants}
            animate="animate"
            position="absolute"
            top={3}
            left={3}
            zIndex={3}
            bg="green.500"
            color="white"
            px={2}
            py={1}
            borderRadius="full"
            fontSize="xs"
            fontWeight="bold"
            display="flex"
            alignItems="center"
            gap={1}
            boxShadow="0 2px 8px var(--color-compat-high-glow-40)"
          >
            <Sparkles size={12} />
            TasteMatch
          </MotionBadge>
        )}
      </Box>
      <Box
        p={{ base: 3, lg: 4 }}
        flex="1"
        display="flex"
        flexDirection="column"
      >
        <Flex
          justifyContent="space-between"
          alignItems="baseline"
        >
          <HStack spacing={1} alignItems="baseline" mr={2} flex={1}>
            <Heading
              ref={titleRef}
              mb={{ base: 1, lg: 2 }}
              lineHeight={1.2}
              fontSize={{ base: '1.125rem', lg: '1.25rem'}}
              fontWeight={700}
              overflow="hidden"
              textOverflow="ellipsis"
              display={'-webkit-box'}
              sx={{
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              <ChakraLink
                as={Link}
                {...nameLinkProps}
                // Preload off (CD decision): the router default is
                // `defaultPreload: 'intent'` with a 0ms stale time, which would
                // refetch on every hover/touch of every card in a long list.
                preload={false}
                onClick={handleNameLinkClick}
                color="inherit"
                textDecoration="none"
                _hover={{ textDecoration: 'none' }}
                // The Heading clips overflow (line-clamp), so the default outside ring
                // (offset 2px) is cut off. A negative offset draws it inside the link box.
                sx={{ _focusVisible: { ...FOCUS_VISIBLE_RING, outlineOffset: '-2px', borderRadius: '4px' } }}
              >
                {restaurant.name}
              </ChakraLink>
            </Heading>
            {restaurant.isVerified && (
              <BadgeCheck size={badgeSize} color="var(--chakra-colors-blue-500)" style={{ flexShrink: 0, position: 'relative', top: badgeTop }} />
            )}
          </HStack>
          {formattedDistance === null ? (
            <Badge
              data-testid="distance-unavailable"
              borderRadius="full"
              px={{ base: '1', lg: '2' }}
              fontSize={{ base: 'xs', lg: 'sm' }}
              bg="gray.100"
              color="gray.600"
            >
              No distance
            </Badge>
          ) : (
            <Badge
              borderRadius="full"
              px={{ base: '1', lg: '2' }}
              fontSize={{ base: 'xs', lg: 'sm' }}
              bg="var(--color-primary-purple)"
              color="white"
            >
              {formattedDistance}
            </Badge>
          )}
        </Flex>
        <Box
          color="var(--card-text-2)"
          mb={{ base: 1, lg: 2 }}
          lineHeight={1.4}
          fontSize={{ base: '0.8rem', lg: '0.875rem' }}
          fontWeight={400}
          overflow="hidden"
          textOverflow="ellipsis"
          display={'-webkit-box'}
          sx={{
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical'
          }}
        >
          {hasValidAddressParts(restaurant.addressParts) ? (
            <>
              <Text>{restaurant.addressParts.address1}</Text>
              <Text>{restaurant.addressParts.address2}</Text>
              <Text>{restaurant.addressParts.address3}</Text>
            </>
          ) : (
            <Text noOfLines={3}>{restaurant.address}</Text>
          )}
        </Box>
        <Text
          color="var(--card-text-2)"
          mb={{ base: 3, lg: 4 }}
          fontSize={'sm'}
          fontWeight={400}
          lineHeight={1.4}
          noOfLines={3}
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {restaurant.description ? restaurant.description : 'No description available...'}
        </Text>

        {/* Friend activity - social proof */}
        {restaurant.friendActivity && restaurant.friendActivity.length > 0 && (
          <Box mt={2}>
            <FriendAvatars
              friends={restaurant.friendActivity}
              maxAvatars={3}
              size="xs"
            />
          </Box>
        )}

        <Divider mt="auto" mb={{ base: 2, lg: 3 }} borderColor="var(--card-divider)" />
        <SimpleGrid columns={3} gap={2} width="100%">
          <CompactActionButton
            layout="fill"
            icon={Smile}
            tokenVar="var(--color-icon-gold)"
            tint12="var(--color-icon-gold-tint-12)"
            tint18="var(--color-icon-gold-tint-18)"
            isActive={restaurant.selfRated}
            label={restaurant.selfRated ? 'Rated' : 'Rate'}
            ariaLabel={restaurant.selfRated ? 'Rated — update rating' : 'Rate restaurant'}
            ariaPressed={restaurant.selfRated}
            onClick={handleRateClick}
          />
          <CompactActionButton
            layout="fill"
            icon={BookmarkCheck}
            tokenVar="var(--color-icon-teal)"
            tint12="var(--color-icon-teal-tint-12)"
            tint18="var(--color-icon-teal-tint-18)"
            isActive={restaurant.isBookmarked}
            label={restaurant.isBookmarked ? 'Saved' : 'Save'}
            ariaLabel={restaurant.isBookmarked ? 'Saved' : 'Save to list'}
            ariaPressed={restaurant.isBookmarked}
            onClick={handleBookmarkClick}
            iconStyle={{ transition: bookmarkIconTransition }}
          />
          <CompactActionButton
            layout="fill"
            icon={Share2}
            tokenVar="var(--color-icon-share)"
            tint12="var(--color-icon-share-tint-12)"
            tint18="var(--color-icon-share-tint-18)"
            label="Share"
            ariaLabel="Share restaurant"
            onClick={handleShareClick}
          />
        </SimpleGrid>
      </Box>
      <RestaurantRatingModal restaurantId={restaurant.id} isOpen={isRatingModalOpen} onClose={() => setIsRatingModalOpen(false)} />
      <AddToListModal
        isOpen={isAddToListModalOpen}
        onClose={() => setIsAddToListModalOpen(false)}
        restaurant={restaurant}
      />
      <CompatibilityModal
        restaurant={restaurant}
        friendCount={restaurant.friendActivity?.length ?? 0}
        isOpen={isCompatibilityModalOpen}
        onClose={() => setIsCompatibilityModalOpen(false)}
      />
    </MotionBox>
  );
}

// Memoize to prevent unnecessary re-renders when parent state changes
export default React.memo(RestaurantCard, (prevProps, nextProps) => {
  // Only re-render when these specific properties change
  return (
    prevProps.restaurant.id === nextProps.restaurant.id &&
    prevProps.restaurant.rating === nextProps.restaurant.rating &&
    prevProps.restaurant.selfRated === nextProps.restaurant.selfRated &&
    prevProps.restaurant.isBookmarked === nextProps.restaurant.isBookmarked &&
    prevProps.restaurant.isVerified === nextProps.restaurant.isVerified &&
    prevProps.restaurant.images?.length === nextProps.restaurant.images?.length &&
    prevProps.restaurant._calculatedDistance === nextProps.restaurant._calculatedDistance &&
    prevProps.index === nextProps.index
  );
});
